import { useState, type FormEvent } from "react";
import Breadcrumb from "@/components/feature/Breadcrumb";
import usePageSeo from "@/hooks/usePageSeo";

const FORM_ACTION = "https://readdy.ai/api/form/dahgiu3oh653ivfvnukg";

const contactItems = [
  {
    icon: "ri-mail-line",
    label: "电子邮箱",
    value: "hello@tadpolegua.com",
    href: "mailto:hello@tadpolegua.com",
  },
  {
    icon: "ri-time-line",
    label: "回复时间",
    value: "通常 2 个工作日内回复",
  },
  {
    icon: "ri-lightbulb-line",
    label: "话题推荐",
    value: "附上话题名称 + 关键时间点，整理效率更高",
  },
  {
    icon: "ri-shield-check-line",
    label: "内容边界",
    value: "不接收个人隐私与未经核实的指控类信息",
  },
];

export default function Contact() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  usePageSeo({
    title: "联系我们 | 蝌蚪吃瓜 话题推荐与反馈",
    description:
      "联系蝌蚪吃瓜：推荐值得考古的话题、反馈内容问题或提出合作想法。附上话题名称与关键时间点，整理团队会评估信息完整度。",
    keywords: "蝌蚪吃瓜,联系我们,话题推荐,内容反馈",
    canonical: "/contact",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "ContactPage",
        name: "联系我们 - 蝌蚪吃瓜",
        url: "https://haeremai.cn/contact",
        inLanguage: "zh-CN",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://haeremai.cn/" },
          { "@type": "ListItem", position: 2, name: "联系我们", item: "https://haeremai.cn/contact" },
        ],
      },
    ],
  });

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);

    const honeypot = String(data.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    data.forEach((value, key) => {
      if (key === "website_alt") return;
      if (typeof value === "string" && value.trim() !== "") {
        payload.append(key, value);
      }
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_ACTION, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const code = parsed?.code;
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || "";
      const isSpam =
        /spam/i.test(serverMsg) || /form data is spam/i.test(responseText);
      const success = response.ok && !isSpam && (parsed ? code === "OK" : true);

      if (!success) {
        setStatus("error");
        setFormError(serverMsg || "提交未成功，请稍后重试或直接发送邮件。");
        return;
      }

      setStatus("success");
      form.reset();
    } catch {
      setStatus("error");
      setFormError("网络似乎不太稳定，请检查连接后重试。");
    }
  };

  return (
    <div className="bg-background-50">
      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-[1280px] px-4 py-10 md:px-6 md:py-14">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "联系我们" }]} />
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
            想推荐一个话题？留言给我们
          </h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            无论是推荐值得考古的旧话题，还是对整理方式有建议，都可以通过下面的表单告诉我们。
            附上你记得的关键时间点，整理团队会更快评估。
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-[1280px] px-4 py-12 md:px-6 md:py-16">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[380px_1fr]">
          <aside className="flex flex-col gap-4">
            {contactItems.map((item) => (
              <div
                key={item.label}
                className="flex items-start gap-4 rounded-xl border border-background-200 bg-background-50 p-5"
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-accent-100 text-accent-700">
                  <i className={`${item.icon} text-xl`}></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-500">{item.label}</p>
                  {item.href ? (
                    <a
                      href={item.href}
                      rel="nofollow"
                      className="mt-1 block text-sm font-medium text-primary-700 hover:underline"
                    >
                      {item.value}
                    </a>
                  ) : (
                    <p className="mt-1 text-sm text-foreground-800">{item.value}</p>
                  )}
                </div>
              </div>
            ))}
          </aside>

          <div className="rounded-2xl border border-background-200 bg-background-50 p-6 md:p-8">
            {status === "success" ? (
              <div className="flex flex-col items-center py-10 text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-accent-100 text-accent-700">
                  <i className="ri-checkbox-circle-line text-3xl"></i>
                </span>
                <h2 className="mt-5 font-heading text-xl font-bold text-foreground-950">
                  留言已收到，感谢分享
                </h2>
                <p className="mt-3 max-w-md text-sm leading-relaxed text-foreground-600">
                  整理团队会在评估后决定是否纳入考古计划。若信息完整，你可能会在后续合集里看到它。
                </p>
                <button
                  type="button"
                  onClick={() => setStatus("idle")}
                  className="mt-6 rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-700"
                >
                  再推荐一个
                </button>
              </div>
            ) : (
              <form data-readdy-form id="tadpole-contact-form" onSubmit={handleSubmit}>
                <h2 className="font-heading text-lg font-bold text-foreground-950">
                  留言 / 话题推荐
                </h2>
                <p className="mt-2 text-sm text-foreground-500">带 * 的为必填项。</p>

                <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="name" className="mb-1.5 block text-sm text-foreground-800">
                      称呼 *
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      placeholder="怎么称呼你"
                      className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="mb-1.5 block text-sm text-foreground-800">
                      邮箱 *
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      placeholder="name@example.com"
                      className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
                    />
                  </div>
                </div>

                <div className="mt-5">
                  <label htmlFor="topic" className="mb-1.5 block text-sm text-foreground-800">
                    想推荐的话题
                  </label>
                  <input
                    id="topic"
                    name="topic"
                    type="text"
                    placeholder="例如：某场活动的完整现场素材"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
                  />
                </div>

                <div className="mt-5">
                  <label htmlFor="message" className="mb-1.5 block text-sm text-foreground-800">
                    留言内容 *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    placeholder="说说你记得的时间点，或想补充的线索（500 字以内）"
                    className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm leading-relaxed text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
                  ></textarea>
                </div>

                <div className="form-guard-alt" aria-hidden="true">
                  <label htmlFor="website_alt">网址备用字段</label>
                  <input
                    id="website_alt"
                    name="website_alt"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                </div>

                {status === "error" && (
                  <p className="mt-4 flex items-start gap-2 rounded-md border border-primary-300 bg-primary-50 px-3.5 py-3 text-sm text-primary-800">
                    <i className="ri-error-warning-line mt-0.5"></i>
                    {formError || "提交失败，请稍后重试。"}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="mt-6 flex w-full items-center justify-center gap-2 rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto"
                >
                  <i className={status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"}></i>
                  {status === "submitting" ? "提交中…" : "提交留言"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}